"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useScrollRestoration = useScrollRestoration;
const react_1 = require("react");
const SCROLL_KEY_PREFIX = 'scroll:';
/**
 * 捲動管理（R16）：放在 Router 裡的 ScrollManager 呼叫，傳入 useLocation()、useNavigationType()
 * （router 的值由呼叫端傳入：utils 依賴的 react-router-dom 和各前端不同版本，在這裡呼叫會找不到 Router）
 * - 重新整理、瀏覽器上一頁／下一頁、麵包屑回列表（帶 RESTORE_SCROLL_STATE）：回到離開時的捲動位置
 * - 點連結進新頁面：捲回頂端；同一頁只改網址參數（分頁籤、頁碼）不動
 * - 捲動時把位置存到 sessionStorage，依網址（路徑＋參數）分開記：列表第 3 頁和第 1 頁各記各的
 * - 用「上一個路徑」判斷換頁，不用「是否首次」：開發模式 StrictMode 會重跑 effect
 */
function useScrollRestoration(location, navigationType) {
    const url = location.pathname + location.search;
    const urlRef = (0, react_1.useRef)(url);
    urlRef.current = url;
    const prevPathname = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        if ('scrollRestoration' in window.history)
            window.history.scrollRestoration = 'manual';
        const save = () => {
            try {
                sessionStorage.setItem(SCROLL_KEY_PREFIX + urlRef.current, String(window.scrollY));
            }
            catch { /* sessionStorage 不可用時略過 */ }
        };
        window.addEventListener('scroll', save, { passive: true });
        return () => window.removeEventListener('scroll', save);
    }, []);
    (0, react_1.useLayoutEffect)(() => {
        const prev = prevPathname.current;
        prevPathname.current = location.pathname;
        if (prev === location.pathname)
            return;
        const restore = prev === null || navigationType === 'POP' || location.state?.restoreScroll;
        let saved = 0;
        if (restore) {
            try {
                saved = Number(sessionStorage.getItem(SCROLL_KEY_PREFIX + url)) || 0;
            }
            catch { /* sessionStorage 不可用時略過 */ }
        }
        if (!saved) {
            if (prev !== null)
                window.scrollTo(0, 0);
            return;
        }
        // 列表資料可能還在載入、頁面還不夠高：3 秒內每個畫面重試，到位、或使用者自己捲動／按鍵就停
        let frame = 0;
        const start = performance.now();
        const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
        const stop = () => {
            cancelAnimationFrame(frame);
            userEvents.forEach(type => window.removeEventListener(type, stop));
        };
        const apply = () => {
            window.scrollTo(0, saved);
            if (Math.abs(window.scrollY - saved) > 1 && performance.now() - start < 3000)
                frame = requestAnimationFrame(apply);
            else
                stop();
        };
        userEvents.forEach(type => window.addEventListener(type, stop, { passive: true }));
        apply();
        return stop;
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
}
